import { notify } from "@general-backoffice/core";
import type { ReferralCode, ReferralCodeCreateInput, ReferralCodeQueryParams, StrapiId } from "@investhinks/api-client";

import { referralCodesEndpoints } from "../../../api";
import { useStrapiOne, type UseStrapiOneOptions } from "../../../hooks/strapi";
import getErrorMessage from "../../../utils/getErrorMessage";
import handleError from "../../utils/handleError";

type UseReferralCodeProps = Omit<UseStrapiOneOptions<ReferralCode>, "id" | "loadItem" | "submitItem" | "deleteItem">;

const useReferralCode = (id?: StrapiId, props?: UseReferralCodeProps) => {
  const {
    item: referralCode,
    setItem: setReferralCode,
    load,
    isLoading,
    setLoading,
    ...other // isLoading, setLoading, load, update, remove
  } = useStrapiOne({
    id,
    ...props,
    loadItem: loadReferralCode,
    submitItem: submitReferralCode,
  });

  return {
    referralCode,
    setReferralCode,
    load,
    isLoading,
    setLoading,
    ...other,
  };
};

export const loadReferralCode = async (id: StrapiId | undefined, _params?: ReferralCodeQueryParams) => {
  // Note: API doesn't have a single referral code getter, so we load all codes and filter
  // TODO: Add getReferralCode(id) endpoint to api-client when backend supports it
  try {
    if (!id) return undefined;
    const { data: codes } = await referralCodesEndpoints.getMyReferralCodes();
    return codes?.find((c) => c.id === id || String(c.id) === String(id));
  } catch (err: unknown) {
    handleError(getErrorMessage(err, "Error while loading referral code"));
  }
};

export const submitReferralCode = async (referralCode: Partial<ReferralCode> | null) => {
  const isNew = !referralCode?.id;
  // Note: API doesn't have update endpoint for referral codes, only create
  const errMsg = `Referral code ${isNew ? "creation" : "update"} failed`;
  if (!referralCode) throw new Error(errMsg);

  if (!isNew) {
    // TODO: Add updateReferralCode endpoint to api-client when backend supports it
    throw new Error("Updating referral codes is not supported by the API");
  }

  try {
    const { name, code } = referralCode;
    // Note: referrer is automatically set by the server from the logged-in user
    const body: ReferralCodeCreateInput = {
      code: code ?? "",
      name: name ?? undefined,
    };

    const { data } = await referralCodesEndpoints.createReferralCode(body);

    notify.success("Referral code creation finished correctly");
    return data;
  } catch (err: unknown) {
    handleError(getErrorMessage(err, errMsg));
  }
};

export default useReferralCode;
